// crewbit-admin/src/hooks/useUpdateBusinessSchedulePreferences.ts
//
// §Settings Phase 2A — admin mirror of mobile hook. Mobile lives at
// crewbit-mobile/hooks/useUpdateBusinessSchedulePreferences.ts. Body decisions
// (RPC call, partial-update args, COALESCE-preserve semantics) are identical;
// admin diverges only in (a) authStore-internal businessId resolution and
// (b) {mutate, loading, error:string} export shape to preserve the existing
// ScheduleSettingsPage consumer contract.
//
// Replaces the pre-Phase-2A raw `.update()` on `business_schedule_preferences`
// — the table is now SECDEF-only via the update_business_schedule_preferences
// RPC (Migration 20260519014053).

import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

export interface UpdateBusinessSchedulePreferencesInput {
  walkInEnabled?: boolean;
  leaseEndMarkersEnabled?: boolean;
  dropOffWindowsEnabled?: boolean;
}

export interface UpdateBusinessSchedulePreferencesResult {
  success: boolean;
  error?: string;
}

export function useUpdateBusinessSchedulePreferences() {
  const businessId = useAuthStore((s) => s.business?.id);
  const [loading, setLoading] = useState(false);

  const mutate = useCallback(async (
    input: UpdateBusinessSchedulePreferencesInput,
  ): Promise<UpdateBusinessSchedulePreferencesResult> => {
    if (!businessId) return { success: false, error: 'No active business.' };
    if (input.walkInEnabled === undefined
        && input.leaseEndMarkersEnabled === undefined
        && input.dropOffWindowsEnabled === undefined) {
      return { success: true };
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('update_business_schedule_preferences', {
        p_business_id: businessId,
        p_walk_in_enabled: input.walkInEnabled ?? null,
        p_lease_end_markers_enabled: input.leaseEndMarkersEnabled ?? null,
        p_drop_off_windows_enabled: input.dropOffWindowsEnabled ?? null,
      });
      if (error) return { success: false, error: error.message };
      const result = data as { success: boolean; error?: { code: string } } | null;
      if (!result?.success) {
        return { success: false, error: result?.error?.code ?? 'UNKNOWN' };
      }
      return { success: true };
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  return { mutate, loading };
}
